import moment, { Moment } from "moment/moment";

import { CarbonProgramResult, CropItemData, PRACTICE_SLUG, PracticeFCPO, PracticeSlug } from "models";

import { FertilizerItemData, TillageItemData, TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";

export const determinePracticeEndDate = (
  currentPracticeItem: TimelineCalendarItemsProps<TillageItemData | FertilizerItemData>,
  practiceItems: TimelineCalendarItemsProps<TillageItemData | FertilizerItemData>[],
  cropItems: TimelineCalendarItemsProps<CropItemData>[]
): Moment => {
  const MAX_MONTHS = 5;
  // The end date of the crop should be the ende date of the practice or max 5 months

  // Search for cropItems over cropId
  const assignedCrop = cropItems.find((cropItem) => cropItem?.cropItemId === currentPracticeItem?.cropItemId);
  // We need this because there can be a cropId match but the dates do not match. Use case: Corp.seedingDate= 2023-01-01 and practice.endDate = 2025-01-01
  // Including start and end date by using "[]"
  const assignedCropIsInPracticeApplicationRange = currentPracticeItem.start_time.isBetween(assignedCrop?.start_time, assignedCrop?.end_time, "days", "[]");

  if (assignedCrop && assignedCropIsInPracticeApplicationRange) {
    const months = assignedCrop.end_time.diff(currentPracticeItem.start_time, "months");
    if (months < MAX_MONTHS && months > 0) currentPracticeItem.end_time = assignedCrop.end_time;
  }

  // Filter out the assignedCrop
  const cropItemsWithoutAssignedCrop = cropItems.filter((cropItem) => cropItem.id !== assignedCrop?.id);
  if (!assignedCropIsInPracticeApplicationRange) {
    const overlappingCrop = cropItemsWithoutAssignedCrop.find((cropItem) =>
      currentPracticeItem.start_time.isBetween(cropItem.start_time, cropItem.end_time, "days")
    );
    if (overlappingCrop) {
      const months = overlappingCrop.end_time.diff(currentPracticeItem.start_time, "months");
      if (months < MAX_MONTHS && months > 0) currentPracticeItem.end_time = overlappingCrop.end_time;
    }
  }

  let remainingMonthsForNextCrop = null;
  let remainingMonthsForNextPracticePractice = null;

  const nextCrop = cropItemsWithoutAssignedCrop.find((cropItem) => {
    // Todo (Alp): Maybe use weeks instead of months
    const months = cropItem.start_time.diff(currentPracticeItem.start_time, "months");
    if (months <= 0) return false;
    remainingMonthsForNextCrop = months;
    return remainingMonthsForNextCrop < MAX_MONTHS;
  });

  const nextPracticePractice = practiceItems.find((practiceItem) => {
    if (practiceItem.id === currentPracticeItem.id) return false;
    const months = practiceItem.start_time.diff(currentPracticeItem.start_time, "months");
    if (months <= 0) return false;
    remainingMonthsForNextPracticePractice = months;
    return remainingMonthsForNextPracticePractice < MAX_MONTHS;
  });

  if (nextCrop) {
    if (!remainingMonthsForNextPracticePractice && remainingMonthsForNextCrop) {
      return nextCrop.start_time;
    }
    if (remainingMonthsForNextPracticePractice && remainingMonthsForNextCrop) {
      if (remainingMonthsForNextCrop < remainingMonthsForNextPracticePractice) {
        return nextCrop.start_time;
      }
    }
  }

  if (nextPracticePractice) {
    return nextPracticePractice.start_time;
  }

  return currentPracticeItem.end_time;
};

export const groupPracticeItemsByFourWeeks = (practiceFCPOS: PracticeFCPO[]) => {
  const sortedByEndDate = practiceFCPOS.sort((a, b) => moment(a.endDate).diff(moment(b.endDate)));
  const practiceGroups: Map<number, PracticeFCPO[]> = new Map();

  let startDate = sortedByEndDate[0].endDate;
  let groupNumber = 0;

  // TODO: (Alp) Only group if they are assigned to the same crop or not assigned at all.

  // This works with the assumption that the practiceFCPOS are sorted by the endDate
  practiceFCPOS.forEach((practice) => {
    const diffInWeeks = moment(practice.endDate).diff(moment(startDate), "days") / 7;
    if (diffInWeeks <= 4) {
      addOrUpdatePracticeGroup(practiceGroups, groupNumber, practice);
      return;
    }
    startDate = practice.endDate;
    groupNumber += 1;
    addOrUpdatePracticeGroup(practiceGroups, groupNumber, practice);
  });

  return practiceGroups;
};

const addOrUpdatePracticeGroup = (PracticeGroups: Map<number, PracticeFCPO[]>, groupNumber: number, practice: PracticeFCPO) => {
  const existingGroup = PracticeGroups.get(groupNumber);
  if (existingGroup) {
    PracticeGroups.set(groupNumber, [...existingGroup, practice]);
  } else {
    PracticeGroups.set(groupNumber, [practice]);
  }
};

export const getBaselineYearsByPracticeType = (carbonProgramResult: CarbonProgramResult): { [k in PracticeSlug]?: number[] } => {
  if (!carbonProgramResult) return {};

  const practicesList = Object.values(PRACTICE_SLUG);

  if ("error" in carbonProgramResult) {
    return practicesList.reduce(
      (acc, practice) => {
        acc[practice] = [];
        return acc;
      },
      {} as { [k in PracticeSlug]?: number[] }
    );
  }

  const baselineYearsByPractice = practicesList.reduce(
    (acc, practice) => {
      const practiceYears = carbonProgramResult.fieldOutcomes?.[practice]?.years;
      if (!practiceYears) {
        acc[practice] = [];
        return acc;
      }

      const baselineYears = Object.entries(practiceYears)
        .filter(([_year, fieldOutcome]) => fieldOutcome.isBaselineYear)
        .map(([year, _fieldOutcome]) => parseInt(year));

      acc[practice] = baselineYears;
      return acc;
    },
    {} as { [k in PracticeSlug]?: number[] }
  );

  return baselineYearsByPractice;
};

export const checkIfMandatoryPractice = (practiceSlug: PracticeSlug, carbonProgramResult: CarbonProgramResult) => {
  if ("error" in carbonProgramResult) return false;
  const practiceOutcomes = carbonProgramResult?.fieldOutcomes?.[practiceSlug];
  if (!practiceOutcomes) return false;

  const requiredCriteria = Object.values(practiceOutcomes.required);
  if (!requiredCriteria) return false;

  return requiredCriteria.some((value) => value);
};
